import { Drawer } from '@mantine/core';
import clsx from 'clsx';
import { useCallback, useRef } from 'react';
import { darkModeRoot } from 'src/app/App.css';
import { SeatingInfo } from 'src/components/common/SeatingInfo';
import { MobileHeaderBar } from 'src/components/mobile/MobileHeader';
import { vars } from 'src/core/themes';
import { globalMobileWrapper } from 'src/core/themes/themeContract.css';
import { Stack } from 'src/core/ui';
import { SolidMoreIcon } from 'src/svgs';
import { Event } from 'src/WebApiController';

import { useSiteTheme } from '../SiteTheme/SiteThemeContext';
import { MobileListingAutoPricingEnabledDisplay } from './components/MobileListingIconDisplay';
import * as styles from './MobileListingDrawer.css';
import {
  useMobileListingDrawerContext,
  ViewMode,
} from './MobileListingDrawerContext';
import { MobileListingDrawerZIndex } from './MobileListingDrawerContext.constants';
import { MobileListingDetailsView } from './views/MobileListingDetailsView';
import { MobileListingSideView } from './views/MobileListingSideView';

const ScrollToTopThreshold = 120;

export const MobileListingDrawer = ({ event }: { event?: Event }) => {
  const { isDarkMode } = useSiteTheme();
  const { viewMode, onNavViewMode, selectedListing } =
    useMobileListingDrawerContext();

  const contentRef = useRef<HTMLDivElement>(null);
  const onHeaderClick = useCallback(() => {
    if (
      contentRef.current &&
      contentRef.current.scrollTop > ScrollToTopThreshold
    ) {
      contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  if (!selectedListing || !event) {
    return;
  }

  return (
    <Drawer.Root
      id={'MobileListingDrawer'}
      opened={!!selectedListing}
      onClose={() => onNavViewMode({ back: true })}
      size="100%"
      position="right"
      className={clsx(globalMobileWrapper, {
        [darkModeRoot]: isDarkMode,
      })}
      zIndex={MobileListingDrawerZIndex.ListingDetails}
    >
      <Drawer.Content ref={contentRef} className={styles.drawerContent}>
        <Drawer.Header className={styles.drawerHeader} onClick={onHeaderClick}>
          <MobileHeaderBar
            onBack={() => onNavViewMode({ back: true })}
            title={
              <SeatingInfo
                section={selectedListing.seating.section ?? ''}
                row={selectedListing.seating.row}
                seatFr={selectedListing.seating.seatFr}
                seatTo={selectedListing.seating.seatTo}
                multiLineDisplay
                labelClassName={styles.seatingLabel}
              />
            }
            actions={
              <Stack direction="row" gap="m" alignItems="center">
                <MobileListingAutoPricingEnabledDisplay
                  isAutoPricingEnabled={selectedListing.isAutoPrc}
                />
                {viewMode === ViewMode.Details && (
                  <SolidMoreIcon
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onNavViewMode({ viewMode: ViewMode.SideView });
                    }}
                    size={vars.iconSize.s}
                    withHoverEffect
                    fill={vars.color.textSecondary}
                  />
                )}
              </Stack>
            }
          />
        </Drawer.Header>
        <Drawer.Body
          className={styles.drawerBody}
          style={{
            backgroundColor:
              viewMode === ViewMode.Details
                ? vars.color.backgroundPrimary
                : vars.color.backgroundSecondary,
          }}
        >
          {viewMode === ViewMode.Details && (
            <MobileListingDetailsView event={event} listing={selectedListing} />
          )}
          {viewMode === ViewMode.SideView && <MobileListingSideView />}
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
};
